import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { showErrorToast, showSuccessToast } from '@ui';
import { translate } from '@utils/i18n';
import { bulkUpdateAttachmentCategory, bulkUpdateAttachmentTechVisibility, bulkUpdateAttachmentVisibility } from '../services/buildingAttachmentsApi';
import { MASS_ACTIONS_NS } from '../constants';
/**
 * Building's own bulk edits — category and the two visibility flags. Delete is not here: it is the
 * one mass action every attachment surface has, so it lives in the shared
 * `useAttachmentDeletion`.
 *
 * Selection semantics: this UI always sends the explicit checked-row `ids` array — it never relies
 * on the endpoint's "absent `ids` falls back to the current filtered/searched set" server-side
 * fallback (`AttachmentsController#resolved_attachments`). That fallback exists for other future
 * callers; this listing is unpaginated and fully client-side (see `fetchBuildingAttachments`'s own
 * doc comment), so there is no "select all matching filter across pages" mode to support — every
 * selection here is already the complete set of checked ids.
 */
export function useAttachmentBulkEdits({ buildingId, selectedIds, clearSelection, invalidate }) {
    const afterSuccess = useCallback((messageKey) => {
        showSuccessToast(translate(messageKey, { namespace: MASS_ACTIONS_NS }));
        clearSelection();
        invalidate();
    }, [clearSelection, invalidate]);
    // On failure: toast AND still invalidate, so the UI re-syncs with the server rather than
    // showing a stale state (MassActions.tsx:73-88 precedent) — selection is left as-is since
    // nothing here optimistically changed it.
    const afterError = useCallback((messageKey) => {
        showErrorToast(translate(messageKey, { namespace: MASS_ACTIONS_NS }));
        invalidate();
    }, [invalidate]);
    const categoryMutation = useMutation({
        mutationFn: (categoryId) => bulkUpdateAttachmentCategory(buildingId, selectedIds, categoryId),
        onSuccess: () => afterSuccess('toasts.category_success'),
        onError: () => afterError('toasts.category_error')
    });
    const visibilityMutation = useMutation({
        mutationFn: (showOnCustomerPortal) => bulkUpdateAttachmentVisibility(buildingId, selectedIds, showOnCustomerPortal),
        onSuccess: () => afterSuccess('toasts.visibility_success'),
        onError: () => afterError('toasts.visibility_error')
    });
    // Bulk counterpart of the per-row `is_private` checkbox — shares the "Update visibility" popover
    // and its success/error copy with `visibilityMutation` above, since both checkboxes are part of
    // the same action from the user's point of view.
    const techVisibilityMutation = useMutation({
        mutationFn: (isPrivate) => bulkUpdateAttachmentTechVisibility(buildingId, selectedIds, isPrivate),
        onSuccess: () => afterSuccess('toasts.visibility_success'),
        onError: () => afterError('toasts.visibility_error')
    });
    return {
        isMutating: categoryMutation.isLoading || visibilityMutation.isLoading || techVisibilityMutation.isLoading,
        updateCategory: useCallback((categoryId) => categoryMutation.mutate(categoryId), [categoryMutation]),
        updateVisibility: useCallback((showOnCustomerPortal) => visibilityMutation.mutate(showOnCustomerPortal), [visibilityMutation]),
        updateTechVisibility: useCallback((isPrivate) => techVisibilityMutation.mutate(isPrivate), [techVisibilityMutation])
    };
}
